const isCypress = typeof Cypress !== 'undefined';

// Get environment variable from Vite or Cypress
const getEnvironmentVariable = (key: string) => {
  return isCypress ? Cypress.env(key) : import.meta.env[key];
};

export const PRIVATE_API = `${getEnvironmentVariable('VITE_PAYWALL_API_URL')}subscriber`;
export const PUBLIC_API = `${getEnvironmentVariable('VITE_ANON_PAYWALL_API_URL')}public`;
export const PAYWALL_URL = getEnvironmentVariable('VITE_PAYWALL_API_URL');
export const ADMIN_API_URL = getEnvironmentVariable('VITE_SUBSCRIBER_API_URL');

export const HUBSPOT_FORM_API = 'https://api.hsforms.com/submissions/v3/integration/submit';
export const SPOTIFY_API_URL = 'https://api.spotify.com/v1';
export const SPOTIFY_AUTH_URL = 'https://accounts.spotify.com/api/token';
export const GEOLOCATION_API = 'https://get.geojs.io/v1/ip/country.json';

export const COOKIES_ACCEPTANCE_LOCAL_STORAGE_KEY = 'eklipx-cookie-consent';

export const PODCAST = {
  spotifyId: '20X3IzoshumvIacMzWawEE',
  urls: {
    spotify: 'https://open.spotify.com/show/20X3IzoshumvIacMzWawEE',
    youtube: 'https://www.youtube.com/@eklipxresearch',
  },
};

export const FREE_ARTICLE_CHUNK_SIZE = 2000;
export const FREE_ARTICLE_MIN_LENGTH_FOR_CTAS = 3000;
export const FREE_ARTICLE_END_CONTENT_BUFFER = 2000;
export const FREE_ARTICLE_IMAGE_EQUIVALENT_CHARACTERS = 750;

export const GDPR_COUNTRY_CODES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
]);

export const PAGE_META_DATA = {
  landing: {
    title: 'Exclusive Intelligence on Chinese & European Gas & LNG Markets',
    description:
      'eklipX Research provides traders with access to a global network of independent market analysts, offering high-quality global and regional insights at a fraction of the cost typically spent by trading companies. We specialize in commodity markets.',
  },
  contact: {
    title: 'Contact Us',
    description:
      'eklipX Research provides traders with access to a global network of independent market analysts, offering high-quality global and regional insights at a fraction of the cost typically spent by trading companies. We specialize in commodity markets.',
  },
  data: {
    title: 'Data',
    description:
      'Explore detailed, up-to-date insights on gas storage and capacity trends including gas in storage, consumption, injection, withdrawal, net withdrawal, working gas volume, and capacity metrics such as contracted, available, and covered capacity. Visualised through interactive graphs for efficient energy market analysis.',
  },
  home: {
    title: 'Commodity Market Intelligence',
    description:
      'eklipX Research provides commodity market intelligence, analysis, and insights to help you make informed decisions.',
  },
  terms: {
    title: 'Terms and Conditions',
    description: 'eklipX Research terms and conditions',
  },
  userAndAuthorTerms: {
    title: 'User and Author Terms',
    description: 'eklipX Research user and author terms',
  },
};
export const ENDPOINTS = {
  getArticlesByRecent: `/articles/recent`,
  getArticleByFeatured: `/articles/featured`,
  getArticleByCategory: `/articles/category`,
  getArticleByAuthor: (authorId: number) => `/authors/${authorId}/articles`,
  getAllCategories: `/categories`,
  getAuthorProducts: (authorId: string) => `/authors/${authorId}/products`,
  getDashboardsByAuthor: (authorId: number) => `/authors/${authorId}/dashboards`,
};

export const PRIVATE_ENDPOINTS = {
  getArticleById: `${PRIVATE_API}/article`,
  getUserSubscriptions: `${PRIVATE_API}/me/subscriptions`,
  getSubscriptionsSettings: `${PRIVATE_API}/me/subscriptions/settings`,
  getWallet: `${PRIVATE_API}/wallet`,
  getUserBypassStatus: `${PRIVATE_API}/me/bypass`,
  purchaseArticle: `${PRIVATE_API}/intents/purchase`,
  createSubscription: `${PRIVATE_API}/subscriptions/create`,
  cancelSubscription: `${PRIVATE_API}/subscriptions/cancel`,
  getNotificationSettingsAuthors: `${PRIVATE_API}/me/notifications/authors`,
  setSubscriptionSettings: (id: string) => `${PRIVATE_API}/me/subscriptions/${id}/settings`,
  validateCheckout: `${PRIVATE_API}/checkout/validate`,
};

export const PUBLIC_ENDPOINTS = {
  getAllContentLabels: `${PUBLIC_API}/content-labels`,
  getAuthorById: `${PUBLIC_API}/authors`,
  getAuthorProducts: (authorId: string) => `${PUBLIC_API}/authors/${authorId}/products`,
  getArticleById: `${PUBLIC_API}/article`,
  getHomepageCategories: `${PUBLIC_API}/homepage-category`,
};

export const ADMIN_ENDPOINTS = {
  getAllowedRoles: `${ADMIN_API_URL}/counterparty/roles/allowed`,
  getSafelistedDomains: `${ADMIN_API_URL}/counterparty/domains/safelist`,
  getEntityInformation: `${ADMIN_API_URL}/counterparty`,
  updateEntityInformation: `${ADMIN_API_URL}/counterparty`,
  getUsers: `${ADMIN_API_URL}/users`,
  invite: `${ADMIN_API_URL}/invite`,
  updateUser: (email: string) => `${ADMIN_API_URL}/users/${email}`,
  deleteUser: (email: string) => `${ADMIN_API_URL}/users/${email}`,
  addUser: (email: string) => `${ADMIN_API_URL}/users/${email}`,
};
export const STRIPE_PUBLIC_KEY = getEnvironmentVariable('VITE_STRIPE_PUBLIC_KEY');

export const CATEGORIES = [
  { id: 1, name: 'Gas, LNG & Carbon', featured: true },
  { id: 8, name: 'Crude Oil & Products', featured: true },
  { id: 2, name: 'Agri & Softs', featured: false },
  { id: 4, name: 'Power and Weather', featured: true },
  { id: 3, name: 'Metals', featured: false },
  { id: 6, name: 'Geopolitics & Macro', featured: true },
];

export const DATA_FILTERS = [
  { id: 'gas-storage', name: 'Gas Storage' },
  { id: 'eurostat-data', name: 'Eurostat Data (free)' },
];

export enum DATA_IFRAMES {
  'gas-storage' = 'https://eklipx.up.railway.app/?embed=true&embed_options=light_theme',
  'eurostat-data' = 'https://app.powerbi.com/view?r=eyJrIjoiNjYxN2JmYTctYmEzYy00NGE3LTk4ZGQtMWI5YzU3OWIxNDQ2IiwidCI6ImIyNGM4YjA2LTUyMmMtNDZmZS05MDgwLTcwOTI2ZjhkZGRiMSIsImMiOjh9',
}

export enum WalletOwnerTypes {
  Author = 'author',
  Subscriber = 'subscriber',
}

export const ErrorCodes = {
  WalletAlreadyExists: 'WalletAlreadyExists',
  WalletNotCreated: 'WalletNotCreated',
  MissingParameter: 'MissingParameter',
  InvalidParameter: 'InvalidParameter',
  InsufficientFunds: 'InsufficientFunds',
  WalletNotFound: 'WalletNotFound',
  UpdateFailed: 'UpdateFailed',
  TransactionFailed: 'TransactionFailed',
  DataUnavailable: 'DataUnavailable',
  AlreadyPurchased: 'AlreadyPurchased',
  AuthorNotOnboarded: 'AuthorNotOnboarded',
} as const;

export const CHECKOUT_ERRORS = {
  AuthorNotOnboarded: {
    title: 'Coming Soon',
    message: 'This article will be available to purchase soon. For more information please contact support@eklipx.io',
  },
  CheckoutFailed: {
    title: 'Purchase Failed',
    message:
      'There was a problem with your purchase of the article. Please contact our support team at support@eklipx.io',
  },
  DataUnavailable: {
    title: 'Purchase Failed',
    message:
      'There was a problem validating your purchase of the article. Please contact our support team at support@eklipx.io',
  },
  UnknownError: {
    title: 'Unknown Error',
    message: 'An unknown error occurred. Please try again later or contact our support team at support@eklipx.io',
  },
} as const;

export enum PackageProductIds {
  GasLngCarbon = 'product_gas_lng_carbon',
  CrudeOilProducts = 'product_crude_oil_products',
  AllAccess = 'product_all_access',
  TrialAccess = 'product_trial_access',
}

export const PackageProducts = [PackageProductIds.AllAccess, PackageProductIds.TrialAccess] as const;

export enum SubscriptionStatus {
  Paid = 'paid',
  PastDue = 'past_due',
  Paused = 'paused',
  Cancelling = 'cancelling',
  Expired = 'expired',
  Trialing = 'trialing',
}

export enum TrialStatus {
  NotTaken = 'not_taken',
  InTrial = 'in_trial',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

export enum SubscriptionCardStatusText {
  Paid = 'Active',
  Cancelling = 'Active - Cancelling',
  Expired = 'Expired',
  Trialing = 'Free Trial',
}

export enum SubscriptionCardButtonText {
  Paid = 'Unsubscribe',
  Cancelling = 'Cancelling',
  Expired = 'Expired',
  Trialing = 'Cancel Trial',
}

export enum SubscriptionSettingsText {
  ManageSubscription = 'Manage Subscription',
}

export enum CancelSubscriptionModalText {
  AccessRetained = 'Access will be retained until',
  Confirmation = 'Are you sure you want to cancel your subscription with',
}

export enum AuthorProfileSubscribeButtonText {
  Subscribed = 'Unsubscribe',
  Unsubscribed = 'Subscribe',
}

export enum SubscribeButtonText {
  Unsubscribe = 'Cancel Subscription',
  Subscribe = 'Subscribe to Analyst',
  FreeTrial = 'Start 7-Day Free Trial',
}

export enum SubscribeButtonLabels {
  AccessRetained = 'Access retained until end of billing period',
  AccessResearchNow = 'Access Research Now',
  TrialThen = 'Then',
  PerMonth = '/month',
  CancelAnytime = 'Cancel anytime',
}

export const ContentType = {
  Articles: 'articles',
  Dashboard: 'dashboard',
  Files: 'files',
} as const;

export const TrackingCustomEvents = {
  click: {
    article: {
      subscribe: 'click_article_subscribe',
    },
    author: {
      login: 'click_author_login',
      subscribe: 'click_author_subscribe',
      unsubscribe: 'click_author_unsubscribe',
    },
    podcast: {
      listenOnSpotify: 'click_podcast_listen_on_spotify',
      listenOnYoutube: 'click_podcast_listen_on_youtube',
    },
    card: {
      article: 'click_card_article',
    },
    category: {
      categoryFilter: 'click_category_filter',
      contentLabelFilter: 'click_content_label_filter',
    },
    navbar: {
      login: 'click_navbar_login',
      logout: 'click_navbar_logout',
      register: 'click_navbar_register',
    },
    paywall: {
      login: 'click_paywall_login',
      register: 'click_paywall_register',
      subscribe: 'click_paywall_subscribe',
    },
    purchases: {
      unsubscribe: 'click_purchases_unsubscribe',
      cancel: 'click_purchases_cancel_trial',
    },
    settings: {
      disable_notifications: 'click_settings_disable_notifications',
      enable_notifications: 'click_settings_enable_notifications',
    },
    cta: {
      contactUs: 'click_cta_contact_us',
      latestArticle: 'click_cta_latest_article',
    },
  },
} as const;
